<template>
  <div>
    <!-- Questions of the template, grouped by control objective on global templates (US 9398) -->
    <div class="v2-card at-questions" qid="questions-section" id="update_question">
      <div class="at-questions__head">
        <h2 qid="questions-table-title">{{ $t('maintenance.questions.label') }}</h2>
        <span v-if="assessment.type == 'control'" class="us-muted">{{ $t('template_view.by_objective') }}</span>
        <GuideButton v-if="getGuide('assessment_template_questions')" :guide="getGuide('assessment_template_questions')"></GuideButton>
        <span class="us-grow"></span>
        <b-button v-if="canEdit && !isEmpty" variant="secondary" size="sm" qid="task-bulk-open" @click="$refs.bulkDialog.open()">
          <font-awesome-icon icon="tasks" class="mr-1"/>{{ $t('task_bulk.open') }}
        </b-button>
        <div class="at-filter">
          <font-awesome-icon icon="search"/>
          <input v-model="questionFilter"
                 :placeholder="$t('template_view.filter_questions')"
                 :aria-label="$t('template_view.filter_questions')"
                 autocomplete="off"
                 qid="questions-filter"
          />
        </div>
      </div>

      <div class="at-qrow at-qrow--head">
        <span></span>
        <span>{{ $t('template_view.columns.reference') }}</span>
        <span>{{ $t('template_view.columns.question') }}</span>
        <!-- Contextual (risk) questions have no controls (US 9480) -->
        <span><template v-if="assessment.type == 'control'">{{ $t('template_view.columns.controls') }}</template></span>
        <span></span>
      </div>

      <!-- Global templates: control objectives that move up and down, each with its questions -->
      <template v-if="assessment.type == 'control'">
        <div v-for="(object, index) in tableItems"
             :key="object.id"
             class="at-group"
             :class="{ 'is-new': isNew(object) }"
             :data-objective="object.id"
             qid="control-objective-group"
        >
          <div class="at-group__head" :class="{ 'is-missing': !object.name }">
            <button type="button"
                    class="at-group__toggle"
                    :aria-expanded="isOpen(object) ? 'true' : 'false'"
                    qid="control-objective-toggle"
                    @click="toggleGroup(object)"
            >
              <font-awesome-icon :icon="isOpen(object) ? 'chevron-down' : 'chevron-right'"/>
              <b>{{ $t('template_view.objective_name', { number: index + 1, name: object.name }) }}</b>
            </button>
            <span class="us-muted">{{ $tc('template_view.questions_count', countOf(object), { count: countOf(object) }) }}</span>
            <span v-if="isNew(object)" class="v2-tag v2-tag--teal" qid="control-objective-imported">{{ $t('template_view.imported_tag') }}</span>
            <span class="us-grow"></span>
            <template v-if="canEdit">
              <button type="button"
                      class="us-icon-btn at-move"
                      :disabled="index === 0 || moving"
                      v-b-tooltip.hover
                      :title="$t('template_view.move.up')"
                      :aria-label="$t('template_view.move.up')"
                      qid="control-objective-up"
                      @click="moveObjective(object, 'up')"
              >
                <font-awesome-icon icon="arrow-up"/>
              </button>
              <button type="button"
                      class="us-icon-btn at-move"
                      :disabled="index === tableItems.length - 1 || moving"
                      v-b-tooltip.hover
                      :title="$t('template_view.move.down')"
                      :aria-label="$t('template_view.move.down')"
                      qid="control-objective-down"
                      @click="moveObjective(object, 'down')"
              >
                <font-awesome-icon icon="arrow-down"/>
              </button>
              <b-dropdown right
                          no-caret
                          variant="link"
                          toggle-class="us-icon-btn at-move"
                          qid="control-objective-more"
              >
                <template #button-content>
                  <font-awesome-icon icon="ellipsis-h"/>
                  <span class="sr-only">{{ $t('users_page.actions.more') }}</span>
                </template>
                <b-dropdown-item :disabled="index === 0 || moving" qid="control-objective-top" @click="moveObjective(object, 'top')">
                  <font-awesome-icon icon="arrow-up" class="mr-2"/>{{ $t('template_view.move.top') }}
                </b-dropdown-item>
                <b-dropdown-item :disabled="index === tableItems.length - 1 || moving" qid="control-objective-bottom" @click="moveObjective(object, 'bottom')">
                  <font-awesome-icon icon="arrow-down" class="mr-2"/>{{ $t('template_view.move.bottom') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item qid="edit-q-modal" @click="showEditCOModal(object)">
                  <font-awesome-icon icon="pen" class="mr-2"/>{{ $t('template_view.objective.edit') }}
                </b-dropdown-item>
                <b-dropdown-item qid="control-objective-add-question" @click="showCreateQFormFor(object)">
                  <font-awesome-icon icon="plus" class="mr-2"/>{{ $t('maintenance.questions.create_button') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item variant="danger" qid="delete-question-modal" @click="showConfirmDeleteCOModal(object)">
                  <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('template_view.objective.delete') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </div>

          <draggable v-show="isOpen(object)"
                     v-model="object.questionItems"
                     :id="'category-' + index"
                     class="dragArea at-group__body"
                     :force-fallback="true"
                     :disabled="!canEdit || !!questionFilter"
                     @start="drag=true"
                     @end="drag=false"
                     @change="postOrderQuestions($event, object)"
                     :options="{group: 'questionItems', handle:'.dnd-drag-me'}">
            <div v-for="item in object.questionItems"
                 v-show="matchesFilter(item)"
                 :key="item.id"
                 class="at-qrow"
                 :id="item.id"
            >
              <span class="dnd-drag-me at-grip" :class="{ 'is-off': !canEdit }"><font-awesome-icon icon="grip-vertical"/></span>
              <span class="at-ref">{{ item.reference }}</span>
              <span class="at-qname">
                <a href="#" class="at-qname__title" qid="question-name" @click.prevent="canEdit && showEditQModal(item)">{{ item.name }}</a>
                <span v-if="plainText(item.description)" class="at-qname__desc">{{ plainText(item.description) }}</span>
              </span>
              <span class="at-controls">
                <span v-for="control in controlChips(item).slice(0, 2)" :key="control" class="v2-tag v2-tag--grey" :title="control">{{ control }}</span>
                <span v-if="controlChips(item).length > 2" class="v2-tag v2-tag--grey" :title="controlChips(item).slice(2).join(', ')">+{{ controlChips(item).length - 2 }}</span>
              </span>
              <span class="at-qrow__actions">
                <template v-if="canEdit">
                  <button type="button" class="us-icon-btn" :aria-label="$t('users_page.actions.edit')" qid="edit-q-modal" @click="showEditQModal(item)">
                    <font-awesome-icon icon="pen"/>
                  </button>
                  <button type="button" class="us-icon-btn" :aria-label="$t('template_view.question.delete')" qid="delete-question-modal" @click="showQConfirmDeleteModal(item)">
                    <font-awesome-icon icon="trash-alt"/>
                  </button>
                </template>
              </span>
            </div>
            <button v-if="canEdit && !questionFilter"
                    slot="footer"
                    type="button"
                    class="at-add"
                    qid="control-objective-add-question-row"
                    @click="showCreateQFormFor(object)"
            >
              <font-awesome-icon icon="plus"/>{{ $t('template_view.add_to_objective', { number: index + 1 }) }}
            </button>
          </draggable>
        </div>
      </template>

      <!-- Contextual templates: one list -->
      <draggable v-else
                 v-model="tableRiskItems"
                 class="dragArea"
                 :force-fallback="true"
                 :disabled="!canEdit || !!questionFilter"
                 @start="drag=true"
                 @end="drag=false"
                 @change="postRiskOrder"
                 :options="{group: 'questionItems', handle:'.dnd-drag-me'}">
        <div v-for="item in tableRiskItems"
             v-show="matchesFilter(item)"
             :key="item.id"
             class="at-qrow"
             :id="item.id"
        >
          <span class="dnd-drag-me at-grip" :class="{ 'is-off': !canEdit }"><font-awesome-icon icon="grip-vertical"/></span>
          <span class="at-ref">{{ item.reference }}</span>
          <span class="at-qname">
            <a href="#" class="at-qname__title" qid="question-name" @click.prevent="canEdit && showEditQModal(item)">{{ item.name }}</a>
            <span v-if="plainText(item.description)" class="at-qname__desc">{{ plainText(item.description) }}</span>
          </span>
          <span class="at-controls"></span>
          <span class="at-qrow__actions">
            <template v-if="canEdit">
              <button type="button" class="us-icon-btn" :aria-label="$t('users_page.actions.edit')" qid="edit-q-modal" @click="showEditQModal(item)">
                <font-awesome-icon icon="pen"/>
              </button>
              <button type="button" class="us-icon-btn" :aria-label="$t('template_view.question.delete')" qid="delete-question-modal" @click="showQConfirmDeleteModal(item)">
                <font-awesome-icon icon="trash-alt"/>
              </button>
            </template>
          </span>
        </div>
        <button v-if="canEdit && !questionFilter"
                slot="footer"
                type="button"
                class="at-add"
                qid="question-add-row"
                @click="showCreateQForm()"
        >
          <font-awesome-icon icon="plus"/>{{ $t('maintenance.questions.create_button') }}
        </button>
      </draggable>

      <div v-if="loading" class="at-questions__empty">
        <font-awesome-icon icon="spinner" class="fa-spin"/>
      </div>
      <div v-else-if="isEmpty" class="at-questions__empty" qid="questions-empty">
        <b>{{ $t('template_view.empty.title') }}</b>
        <span>{{ assessment.type == 'control' ? $t('template_view.empty.text_control') : $t('template_view.empty.text') }}</span>
        <div v-if="canEdit" class="at-questions__empty-actions">
          <button type="button" v-if="assessment.type == 'control' && getClient.aiModule" class="v2-ai-btn api-ai-btn" qid="questions-empty-import" @click="$emit('import')">
            <font-awesome-icon icon="sparkles"/>{{ $t('audit_plan_import.button') }}
          </button>
          <b-button v-if="assessment.type == 'control'" variant="secondary" @click="showCreateControlObjectiveModal()">
            <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('template_view.objective.add') }}
          </b-button>
          <b-button variant="success" qid="question-create-button" @click="showCreateQForm()">
            <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('maintenance.questions.create_button') }}
          </b-button>
        </div>
      </div>
    </div>


    <!-- Delete Assessment Control modal template -->
    <b-modal ref="delete-question" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-q-title">{{ $t('template_view.delete.task_title') }}</h5>
          <button type="button" class="us-icon-btn" :aria-label="$t('maintenance.questions.delete.cancel')" @click="hideQConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <span class="us-remove__icon"><font-awesome-icon icon="trash-alt"/></span>
        <div class="us-found__text">
          <b>{{ itemToDelete.name }}</b>
          <span v-if="itemToDelete.reference">{{ itemToDelete.reference }}</span>
        </div>
      </div>
      <p class="mt-3 mb-0">{{ $t('template_view.delete.cannot_undo') }}</p>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-question-cancel-button" @click="hideQConfirmDeleteModal">
          {{ $t('maintenance.questions.delete.cancel') }}
        </b-button>
        <b-button variant="danger" qid="delete-question-submit-button" @click="confirmDeleteQ(itemToDelete)">
          {{ $t('template_view.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Change a field on many tasks at once (US 9398) -->
    <TaskBulkDialog ref="bulkDialog"
                    :assessment="assessment"
                    :tasks="allTasks"
                    :objectives="assessment.type === 'control' ? tableItems : []"
                    :defaults="templateDefaults"
                    @applied="reloadTasks"
    ></TaskBulkDialog>

    <!-- Create and edit a task (US 9398) -->
    <TaskDialog ref="taskDialog"
                :adapter="taskAdapter"
                :objectives="controlObjectives"
                :objective-required="assessment.type === 'control'"
                :can-create-objective="canCreateObjective"
                :defaults="templateDefaults"
                @create-objective="showCreateControlObjectiveModal"
                @saved="reloadTasks"
    ></TaskDialog>

    <!-- Add new control objective -->
    <b-modal ref="add-control-objective"
             @hide="hideCreateControlObjectiveModal"
             modal-class="task-modal"
             hide-footer
    >
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateControlObjectiveModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="add-co-title">
            {{ $t('maintenance.control_objective.create.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="add-co-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="coFormSubmit" qid="create-co-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-control-objective-name-label">
              {{ $t('maintenance.control_objective.create.name_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
                qid="create-control-objective-form-name"
                type="text"
                v-model="coForm.name"
                :maxLength="255"
                required
                :placeholder="$t('maintenance.control_objective.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-control-objective-description-label">
              {{ $t('maintenance.control_objective.create.description_label') }}
            </label>
            <b-form-textarea
                qid="create-control-objective-form-description"
                v-model="coForm.description"
                rows="6"
                max-rows="6"
                :placeholder="$t('maintenance.control_objective.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
        </div>

        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideCreateControlObjectiveModal"
                            qid="create-control-objective-cancel-button"
                  >
                    {{ $t('maintenance.control_objective.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="create-control-objective-submit-button"
                  >
                        <span v-if="buttonDisabled" class="mr-1">
                          <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                        </span>
                    {{ $t('maintenance.control_objective.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Edit Control Objective modal template -->
    <b-modal ref="edit-control-objective" @hide="hideEditCOModal" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideEditCOModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="update-tp-title">
            {{ $t('maintenance.control_objective.update.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-oc-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="editCOFormSubmit" qid="edit-oc-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-oc-name-label">
              {{ $t('maintenance.control_objective.create.name_label') }}
            </label>
            <span class="text-red">*</span>
            <b-form-input
              qid="edit-oc-form-name"
              type="text"
              v-model="coForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.control_objective.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-control-objective-description-label">
              {{ $t('maintenance.control_objective.create.description_label') }}
            </label>
            <b-form-textarea
                qid="create-control-objective-form-description"
                v-model="coForm.description"
                rows="6"
                max-rows="6"
                :placeholder="$t('maintenance.control_objective.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
        </div>
      </b-form>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideEditCOModal"
                            qid="edit-oc-cancel-button"
                  >
                    {{ $t('maintenance.control_objective.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            @click="editCOFormSubmit"
                            :disabled="buttonDisabled"
                            qid="edit-oc-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('maintenance.control_objective.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Delete Control Objective confirm modal template -->
    <b-modal ref="delete-control-objective" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-co-title">{{ $t('template_view.delete.objective_title') }}</h5>
          <button type="button" class="us-icon-btn" :aria-label="$t('maintenance.control_objective.delete.cancel')" @click="hideConfirmDeleteCOModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <b-alert v-if="error.length" show variant="danger" qid="delete-co-error">{{ error }}</b-alert>
      <div class="us-remove">
        <span class="us-remove__icon"><font-awesome-icon icon="trash-alt"/></span>
        <div class="us-found__text">
          <b>{{ itemToDelete.name }}</b>
          <span>{{ $tc('template_view.questions_count', countOf(itemToDelete), { count: countOf(itemToDelete) }) }}</span>
        </div>
      </div>
      <!-- An objective with tasks can't be deleted; the tasks are moved or deleted first -->
      <div v-if="countOf(itemToDelete)" class="us-note at-defaults__warning mt-3" qid="delete-co-tasks-warning">
        <font-awesome-icon icon="exclamation-triangle"/>
        <span>{{ $tc('template_view.delete.objective_tasks', countOf(itemToDelete), { count: countOf(itemToDelete) }) }}</span>
      </div>
      <p v-else class="mt-3 mb-0">{{ $t('template_view.delete.cannot_undo') }}</p>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-co-cancel-button" @click="hideConfirmDeleteCOModal">
          {{ countOf(itemToDelete) ? $t('template_view.delete.close') : $t('maintenance.control_objective.delete.cancel') }}
        </b-button>
        <b-button v-if="!countOf(itemToDelete)" variant="danger" qid="delete-co-submit-button" :disabled="buttonDisabled" @click="confirmDeleteCO(itemToDelete)">
          <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
          {{ $t('template_view.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import draggable from 'vuedraggable';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import TaskDialog from '@/components/assessment/TaskDialog.vue';
  import TaskBulkDialog from '@/components/assessment/TaskBulkDialog.vue';
  import { templateAdapter } from '@/components/assessment/taskDialogAdapters.js';
  import { parseOptions } from '@/util/dropdownOptions.js';

  export default {
    name: 'QuestionsTable',
    data: function() {
      return {
        assessment: {},
        tableItems: [],
        tableRiskItems: [],
        controlObjectives: [],
        questionFilter: '',
        // Objectives just added by an audit plan import, marked until the page is left (US 9404)
        newObjectives: [],
        scrollToNew: false,
        collapsed: {},
        moving: false,
        drag: false,
        coForm: {
          name: '',
          description: ''
        },
        itemToDelete: {},
        error: '',
        buttonDisabled: false,
        loading: true,
        myGuides: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      }
    },
    components: {
      draggable,
      GuideButton,
      TaskDialog,
      TaskBulkDialog
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      // The template's default drop-downs that tasks can use instead of their own list
      templateDefaults() {
        return {
          response: parseOptions(this.assessment.default_response_options),
          documentation: parseOptions(this.assessment.default_response_options_doc)
        };
      },
      // Every task of the template
      allTasks() {
        return this.assessment.type == 'control' ? _.flatMap(this.tableItems, object => object.questionItems || []) : this.tableRiskItems;
      },
      taskAdapter() {
        return templateAdapter(this.assessment, this.getClient);
      },
      canEdit() {
        return this.isGranted([this.permissions.ASSESSMENT_EDIT, this.permissions.ASSESSMENT_EDIT_OWN], this.assessment.created_by) && (!this.getClient.parent || (this.getClient.parent && this.assessment.partition));
      },
      canCreateObjective() {
        return this.isGranted(this.permissions.CONTROL_OBJECTIVE_CREATE) && (!this.getClient.parent || (this.getClient.parent && this.assessment.partition));
      },
      isEmpty() {
        return this.assessment.type == 'control' ? !this.tableItems.length : !this.tableRiskItems.length;
      }
    },
    watch: {
      // Objective groups load one by one; scroll to the first imported one once it is there
      tableItems(items) {
        if (this.scrollToNew && _.find(items, { id: this.newObjectives[0] })) {
          this.scrollToNew = false;
          this.$nextTick(() => {
            let group = this.$el.querySelector('[data-objective="' + this.newObjectives[0] + '"]');
            if (group) {
              group.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          });
        }
      },
      object: function (value) {
        this.assessment = value;
      }
    },
    methods: {
      // Create and edit open the task dialog (US 9398)
      showCreateQForm() {
        this.loadCO();
        this.$refs.taskDialog.open(null, null);
      },
      showCreateQFormFor(object) {
        this.loadCO();
        this.$refs.taskDialog.open(null, object);
      },
      showEditQModal(item) {
        this.loadCO();
        this.$refs.taskDialog.open(item);
      },
      // Marks the objectives an audit plan import just added and reloads the table
      showImported(objectiveIds) {
        this.newObjectives = objectiveIds || [];
        this.scrollToNew = this.newObjectives.length > 0;
        this.reloadTasks();
      },
      isNew(object) {
        return this.newObjectives.indexOf(object.id) !== -1;
      },
      reloadTasks() {
        if (this.assessment.type === 'control') {
          this.loadQuestions();
        } else {
          this.loadRiskQuestions();
        }
        this.$emit('updated');
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope === code && object.type === 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0]);
          }
        }, function() {});
      },
      isOpen(object) {
        return !this.collapsed[object.id] || !!this.questionFilter;
      },
      toggleGroup(object) {
        this.$set(this.collapsed, object.id, !this.collapsed[object.id]);
      },
      countOf(object) {
        return (object.questionItems || []).length;
      },
      matchesFilter(item) {
        let keyword = this.questionFilter.trim().toLowerCase();
        return !keyword || [item.name, item.reference].some(value => value && value.toLowerCase().indexOf(keyword) !== -1);
      },
      // Descriptions are rich text; the list only shows their first words
      plainText(html) {
        if (!html) {
          return '';
        }
        let element = document.createElement('div');
        element.innerHTML = html;
        return (element.textContent || '').trim();
      },
      // Framework controls first, then linked controls, by reference where they have one
      controlChips(item) {
        return (item.framework_controls || []).concat(item.linked_controls || []).map(control => control.reference || control.name);
      },
      // Moves an objective one place, or to the top or bottom; the server renumbers and logs it
      moveObjective(object, position) {
        let self = this;
        this.moving = true;

        piincHttp.post('assessment-control-objectives/' + object.id + '/move', {
          position: position,
          client: this.getClient.slug
        }).then(function(response) {
          let order = _.map(response.data, 'id');
          self.tableItems = _.sortBy(self.tableItems, item => order.indexOf(item.id));
          _.forEach(self.tableItems, function(item, index) {
            item.order = index + 1;
          });
          self.moving = false;
          self.$emit('updated');
        }, function(error) {
          self.moving = false;
          self.$toastr('error', error.data && error.data.message ? error.data.message : self.$t('users_page.error'));
        });
      },
      showCreateControlObjectiveModal() {
        this.coForm.name = '';
        this.coForm.description = '';
        this.error = '';
        this.$refs['add-control-objective'].show();
      },
      hideCreateControlObjectiveModal() {
        this.coForm.name = '';
        this.coForm.description = '';
        this.error = '';
        this.loadCO();
        this.$refs['add-control-objective'].hide();
      },
      showConfirmDeleteCOModal(object) {
        this.error = '';
        this.itemToDelete = object;

        // this.itemToDelete = object
        this.$refs['delete-control-objective'].show();
      },
      hideConfirmDeleteCOModal() {
        this.itemToDelete = {};
        this.$refs['delete-control-objective'].hide();
      },
      confirmDeleteCO(object) {
        let self = this;
        this.buttonDisabled = true;

        if (object.questionItems && !object.questionItems.length) {
          piincHttp.delete('assessment-control-objectives/' + object.id).then(function() {
            self.hideConfirmDeleteCOModal();
            self.loadQuestions();
            self.$emit('updated');
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('maintenance.control_objective.delete.success'));
          }, function() {});
        }else{
          self.buttonDisabled = false;
          self.error = this.$t('maintenance.questions.delete.linked_assessment_controls_error');
        }
      },
      postRiskOrder() {
        let self = this;
        let order = []
        _.forEach(this.tableRiskItems, function (item, index) {
          order.push({
            id: item.id,
            position: index + 1
          });
        })
        piincHttp.post('questions/order', {
          assessment_id: this.assessment.id,
          order: order
        }).then(function(response) {
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      postOrderQuestions(event, object) {
        let self = this;
        let order = []
        _.forEach(object.questionItems, function (item, index) {
          order.push({
            id: item.id,
            position: index + 1
          });

          if (item.control_objective && item.control_objective.id && item.control_objective.id !== object.id) {
            piincHttp.put('questions/' + item.slug, {
              control_objective_id: object.id,
            }).then(function(response) {
            }, function(error) {
              if (error.status !== 200) {
                self.buttonDisabled = false;
                self.error = error.data.message;
              }
            })
          }
        })


        piincHttp.post('questions/order', {
          assessment_id: this.assessment.id,
          control_objective_id: object.id,
          order: order
        }).then(function(response) {
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      showQConfirmDeleteModal(object) {
        this.itemToDelete = object;
        this.$refs['delete-question'].show();
      },
      hideQConfirmDeleteModal() {
        this.itemToDelete = {};
        this.$refs['delete-question'].hide();
      },
      confirmDeleteQ(object) {
        let self = this;
        piincHttp.delete('questions/' + object.slug).then(function() {
          self.hideQConfirmDeleteModal();

          self.reloadTasks();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.questions.delete.success'))
        }, function() {});
      },
      coFormSubmit(evt) {
        this.buttonDisabled = true;
        // let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        this.coForm.client = this.getClient.slug;
        this.coForm.assessment_id = this.assessment.id;

        piincHttp.post('/assessment-control-objectives', this.coForm).then(function(response) {
          self.hideCreateControlObjectiveModal();
          self.loadQuestions();

          // A new objective made from the task dialog goes straight into it
          self.$refs.taskDialog.setObjective(response.data);

          self.$emit('updated');
          self.buttonDisabled = false;

          self.$toastr('success', self.$t('maintenance.control_objective.create.success'));
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      showEditCOModal(object) {
        this.coForm.slug = object.slug;
        this.coForm.id = object.id;
        this.coForm.name = object.name;
        this.coForm.description = object.description;
        this.$refs['edit-control-objective'].show();
      },
      hideEditCOModal() {
        this.$refs['edit-control-objective'].hide();
      },
      editCOFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        this.coForm.client = this.getClient.slug;
        this.coForm.assessment_id = this.assessment.id;

        if (this.coForm.name && this.coForm.name.length) {
          piincHttp.put('assessment-control-objectives/' + this.coForm.id, this.coForm).then(function(response) {
            self.hideEditCOModal();
            self.loadQuestions();
            self.$emit('updated');
            self.buttonDisabled = false;

            self.$toastr('success', self.$t('maintenance.control_objective.update.success'));
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.buttonDisabled = false;
          this.error = '';
          if (this.coForm.name === '') {
            this.error +=  ' ' + this.$t('maintenance.incidents.incident_categories.create.name_error');
          }
        }
      },
      loadRiskQuestions() {
        let self = this;

        piincHttp.get('questions', { params:
            {
              page: 1,
              per_page: 1,
              assessment: this.assessment.id,
              client: this.getClient.slug
            }
        }).then(function(response) {
          let total = response.data.total_count

          piincHttp.get('questions', { params:
              {
                page: 1,
                per_page: total,
                assessment: self.assessment.id,
                client: self.getClient.slug
              }
          }).then(function(response) {
            self.loading = false;
            self.tableRiskItems = response.data.items;

            // Set number of total items
            self.totalRows = response.data.total_count;
          }, function() {});
        }, function() {});
      },
      loadQuestions() {
        let self = this;

        piincHttp.get('assessment-control-objectives', { params:
          {
            page: 1,
            per_page: 1,
            assessment: this.assessment.id,
            client: this.getClient.slug
          }
        }).then(function(response) {
          let total = response.data.total_count;

          piincHttp.get('assessment-control-objectives', { params:
            {
              page: 1,
              per_page: total,
              assessment: self.assessment.id,
              client: self.getClient.slug
            }
          }).then(function(response) {

            self.tableItems = []
            // Set number of total items
            self.totalRows = response.data.total_count;

            if (!response.data.items.length) {
              self.loading = false;
            }

            _.forEach(response.data.items, function(item, key) {

              piincHttp.get('questions', { params:
                {
                  client: self.getClient.slug,
                  control_objective: item.id,
                  per_page: 100,
                  assessment: self.assessment.id
                }
              }).then(function(responseQuestions) {
                item.questionItems = responseQuestions.data.items;
                self.tableItems.splice(key, 0, item)
                self.tableItems = _.sortBy(self.tableItems, ['order']);
                self.loading = false;
              }, function() {});
            });

          }, function() {});
        }, function() {});
      },
      loadCO() {
        let self = this;
        piincHttp.get('/assessment-control-objectives', { params:
          {
            client: this.getClient.slug,
            assessment: this.assessment.id,
            per_page: 100
          }
        }).then(function(response) {
          self.controlObjectives = response.data.items;
        }, function() {});
      }
    },
    created () {
      this.assessment = this.object;
      if (this.assessment.type === 'control') {
        this.loadQuestions();
        this.loadCO();
      } else {
        this.loadRiskQuestions();
      }
      this.loadGuides('assessment_template_questions');
    }
  }
</script>
